import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { DeviceOverviewStatus, DeviceType, NewPatientListInfo } from '../types';
import { API_BASE_PATH } from './constants';
import { fetchDeviceListing, resolveDeviceType } from './device-listing';
import { getCurrentUser } from './auth-user';
import { toAppError } from './error-mapping';
import { getPortalOrganizationId, toDeviceOverviewStatus } from './portal-adapter';
import { mapDeviceAdapterStatusToPortalStatus } from './pairings.devices';
import { toHealthdotDisplayStatus } from './healthdot-status';
import { parseBackendTimestamp } from './backend-timestamp';

const RECENT_PAIRING_WINDOW_HOURS = 48;

// The listing reports signals, not a status. For a viQtor they fold into the same seven
// values the portal used to send, with the mapping the pairing wizard already applies.
// For a HealthDot they do not: its status is HQS-2138, carried in `deviceState`, and the
// coarse value here exists only so the connected/disconnected icon has something to read.
function getPortalStatusCode(
  client: backend.PortalClientResponse,
  device?: backend.DeviceAdapterDeviceListEntry,
): backend.PortalDeviceStatus | undefined {
  if (!device) return client.deviceStatus?.deviceStatus;
  return mapDeviceAdapterStatusToPortalStatus(device);
}

function getPortalOverviewStatusCode(
  client: backend.PortalClientResponse,
  device?: backend.DeviceAdapterDeviceListEntry,
): DeviceOverviewStatus {
  return toDeviceOverviewStatus(getPortalStatusCode(client, device));
}

function getPortalLastUpdateTimestamp(
  client: backend.PortalClientResponse,
  device?: backend.DeviceAdapterDeviceListEntry,
): string | undefined {
  return (
    device?.connectionStatus?.lastUpdateTimestamp ||
    client.deviceStatus?.lastUpdateTime ||
    undefined
  );
}

function normalizeBatteryLevel(value?: number | null): number | undefined {
  if (value === null || value === undefined) return undefined;

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;

  const rounded = Math.round(numeric);
  if (rounded < 0 || rounded > 100) return undefined;

  return rounded;
}

function getPortalBatteryLevel(
  client: backend.PortalClientResponse,
  device?: backend.DeviceAdapterDeviceListEntry,
): number | undefined {
  const deviceBatteryLevel = normalizeBatteryLevel(device?.batteryStatus?.charge);
  if (deviceBatteryLevel !== undefined) return deviceBatteryLevel;

  return normalizeBatteryLevel(client.deviceStatus?.batteryLevel);
}

function normalizeSerialNumber(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
}

function getPatientName(client: backend.PortalClientResponse): string {
  const firstName = client.personalData?.firstName?.trim() || '';
  const lastName = client.personalData?.lastName?.trim() || '';
  return [firstName, lastName].filter(Boolean).join(' ');
}

function getClinicalUnitName(client: backend.PortalClientResponse): string {
  const clinicalUnitName = client.clinicalUnit?.name?.trim();
  if (clinicalUnitName) return clinicalUnitName;
  return '';
}

function getClinicalUnitGroupName(client: backend.PortalClientResponse): string {
  const clinicalUnitGroupName = client.clinicalUnit?.cohortDetails?.name?.trim();
  return clinicalUnitGroupName || '';
}

function resolveAssignedDevice(
  client: backend.PortalClientResponse,
  devicesById: Map<string, backend.DeviceAdapterDeviceListEntry>,
  devicesByClientId: Map<string, backend.DeviceAdapterDeviceListEntry>,
  devicesBySerialNumber: Map<string, backend.DeviceAdapterDeviceListEntry>,
): backend.DeviceAdapterDeviceListEntry | undefined {
  const personalDevice = client.personalData?.device;
  if (personalDevice?.id) {
    const deviceById = devicesById.get(personalDevice.id);
    if (deviceById) return deviceById;
  }

  const normalizedPersonalDeviceSerial = normalizeSerialNumber(personalDevice?.serialNumber);
  if (normalizedPersonalDeviceSerial) {
    const deviceBySerialNumber = devicesBySerialNumber.get(normalizedPersonalDeviceSerial);
    if (deviceBySerialNumber) return deviceBySerialNumber;
  }

  return devicesByClientId.get(client.id);
}

// A device coming back under pairedSince is not by itself proof of a live pairing to
// this client: the window says something about the device, not about who holds it,
// and DeviceResponse in the portal OpenAPI carries neither `assigned` nor `clientId`.
// So where the backend does send them, hold it to them; where it does not, the
// filtered /devices result is all there is to go on. Absent means unknown, not false
// -- reading it as false would empty the list against a backend that matches the spec.
function isCurrentPairing(
  client: backend.PortalClientResponse,
  device?: backend.DeviceAdapterDeviceListEntry,
): boolean {
  if (!device) return false;
  if (device.assigned === false) return false;
  if (device.clientId && device.clientId !== client.id) return false;
  return true;
}

// Every page is read before matching: a device on page two is as much this ward's patient
// as one on page one, and a dropped device takes its patient off the screen (HQS-2369).
// The window is what separates this listing from the pairing one -- see fetchDeviceListing.
async function fetchDevicesInPairingWindow(
  hcoId: string,
): Promise<backend.DeviceAdapterDeviceListEntry[]> {
  return fetchDeviceListing({ hcoId, pairedSince: RECENT_PAIRING_WINDOW_HOURS });
}

export async function getPatients(): Promise<NewPatientListInfo[]> {
  try {
    const user = await getCurrentUser();
    const hcoId = getPortalOrganizationId(user);
    if (!hcoId) return [];

    const [{ data: clients }, devices] = await Promise.all([
      axios.get<backend.PortalClientResponse[]>(`${API_BASE_PATH}/clients`, {
        params: {
          hcoId,
        },
      }),
      fetchDevicesInPairingWindow(hcoId),
    ]);

    const devicesById = new Map<string, backend.DeviceAdapterDeviceListEntry>();
    const deviceByClientId = new Map<string, backend.DeviceAdapterDeviceListEntry>();
    const devicesBySerialNumber = new Map<string, backend.DeviceAdapterDeviceListEntry>();

    devices.forEach((device) => {
      devicesById.set(device.id, device);

      if (device.assigned && device.clientId) {
        deviceByClientId.set(device.clientId, device);
      }

      const normalizedSerialNumber = normalizeSerialNumber(device.serialNumber);
      if (normalizedSerialNumber && !devicesBySerialNumber.has(normalizedSerialNumber)) {
        devicesBySerialNumber.set(normalizedSerialNumber, device);
      }
    });

    const patientsWithDevice = clients
      .filter((entry) => Boolean(entry.externalPatientId))
      .map((entry) => ({
        entry,
        assignedDevice: resolveAssignedDevice(
          entry,
          devicesById,
          deviceByClientId,
          devicesBySerialNumber,
        ),
      }))
      // The last 48 hours is what this screen is (HQS-1503, widened from 24 hours by
      // HQS-2438), so /devices is always asked for that window and membership always
      // follows the filtered result. The client's own embedded device reference cannot
      // decide it: that reference outlives the pairing which created it, and believing
      // it is what put pairings older than the window on the screen (HQS-2369).
      .filter(({ entry, assignedDevice }) => isCurrentPairing(entry, assignedDevice));

    return patientsWithDevice.map<NewPatientListInfo>(({ entry, assignedDevice }, index) => {
      const startDate = parseBackendTimestamp(getPortalLastUpdateTimestamp(entry, assignedDevice));
      const patientName = getPatientName(entry);
      const batteryLevel = getPortalBatteryLevel(entry, assignedDevice);
      const deviceType = resolveDeviceType(
        assignedDevice,
        entry.personalData?.device?.serialNumber,
      );
      // HQS-2142 applies to Healthdots only. A viQtor keeps the seven-value vocabulary,
      // which its battery and worn indicators are already read against.
      const healthdotStatus =
        deviceType === DeviceType.Healthdot
          ? toHealthdotDisplayStatus(assignedDevice?.deviceState)
          : undefined;

      return {
        id: index + 1,
        hospitalPatientId: entry.externalPatientId || entry.id,
        ...(patientName ? { patientName } : {}),
        clinicalUnitName: getClinicalUnitName(entry),
        clinicalUnitGroupName: getClinicalUnitGroupName(entry),
        ...(startDate ? { startDate } : {}),
        // For a Healthdot this coarse value drives the connected icon only; the text the
        // row shows comes from healthdotStatus. "Status unavailable" must not read as
        // monitored, so anything but `connected` is disconnected here.
        deviceStatus: healthdotStatus
          ? healthdotStatus === 'connected'
            ? 'OK'
            : 'DISCONNECTED'
          : getPortalOverviewStatusCode(entry, assignedDevice),
        ...(healthdotStatus ? { healthdotStatus } : {}),
        deviceType,
        ...(batteryLevel !== undefined ? { batteryLevel } : {}),
      };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    throw toAppError(err);
  }
}
